import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Close, EditOutlined, HomeRounded, ScheduleRounded, TuneRounded } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteField, FieldValue } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../../firebase/firebase';
import {
  fetchAllEmployees,
  fetchServices,
  getCurrentBusinessConfig,
  updatePreferencesConfig,
  updateService,
} from '../../firebase/firebase-delegate';
import { formatMinutes } from '../../time/timeUtils';
import { Employee, Service } from '../../types';
import { resolveAdminBookingUxConfig } from '../../utils/bookingUxConfig';
import LoadingSpinner from '../global/LoadingSpinner';
import { formatServicePrice } from '../../utils/servicePrice';
import ServiceFormDialog from './ServiceFormDialog';

const ServiceThumbnail: React.FC<{ service: Service }> = ({ service }) => {
  const query = useQuery({
    queryKey: ['service-image-url', service.imagePath],
    queryFn: () => getDownloadURL(ref(storage, service.imagePath!)),
    enabled: Boolean(service.imagePath),
    staleTime: 1000 * 60 * 30,
    retry: false,
  });
  return (
    <Box sx={{ position: 'relative', aspectRatio: '16 / 10', overflow: 'hidden', bgcolor: 'rgba(25,118,210,.08)' }}>
      {query.data ? (
        <Box component="img" src={query.data} alt="" loading="lazy"
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : (
        <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center',
          background: 'linear-gradient(135deg, rgba(25,118,210,.16), rgba(116,212,255,.22))' }}>
          <Typography fontWeight={900} fontSize="2.5rem" color="primary.main" sx={{ opacity: .7 }}>
            {service.title.slice(0, 1).toUpperCase()}
          </Typography>
        </Box>
      )}
      {service.imagePath && service.showOnHomepage !== false ? (
        <Chip size="small" icon={<HomeRounded />} label="On homepage"
          sx={{ position: 'absolute', left: 10, top: 10, fontWeight: 800, bgcolor: 'rgba(255,255,255,.92)' }} />
      ) : null}
    </Box>
  );
};

const ServiceCard: React.FC<{
  service: Service;
  staffCount: number;
  staffLabel: string;
  staffLabelSingular: string;
  onEdit: () => void;
}> = ({ service, staffCount, staffLabel, staffLabelSingular, onEdit }) => (
  <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 4, border: '1px solid', borderColor: 'rgba(15,23,42,.08)',
    boxShadow: '0 10px 30px rgba(15,23,42,.06)', display: 'flex', flexDirection: 'column',
    transition: 'transform 160ms ease, box-shadow 160ms ease',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 16px 40px rgba(15,23,42,.10)' } }}>
    <ServiceThumbnail service={service} />
    <Stack spacing={1.25} sx={{ p: 2, flex: 1 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Typography fontWeight={850} fontSize="1.05rem" lineHeight={1.3}>{service.title}</Typography>
        <Typography fontWeight={900} color="primary.main" sx={{ whiteSpace: 'nowrap' }}>
          {formatServicePrice(service)}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" icon={<ScheduleRounded />} label={formatMinutes(service.time)} sx={{ fontWeight: 700 }} />
        {service.category ? <Chip size="small" label={service.category} variant="outlined" sx={{ fontWeight: 700 }} /> : null}
      </Stack>
      {service.description ? (
        <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{service.description}</Typography>
      ) : null}
      <Box sx={{ flex: 1 }} />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" color={staffCount ? 'text.secondary' : 'error.main'} fontWeight={700}>
          {staffCount ? `${staffCount} ${staffCount === 1 ? staffLabelSingular : staffLabel}`.toLowerCase()
            : `No ${staffLabel.toLowerCase()} assigned`}
        </Typography>
        <Button size="small" startIcon={<EditOutlined />} onClick={onEdit} sx={{ fontWeight: 800 }}>Edit</Button>
      </Stack>
    </Stack>
  </Paper>
);

const CategoriesDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  categories: string[];
  busy: boolean;
  onAdd: (category: string) => void;
  onRemove: (category: string) => void;
}> = ({ open, onClose, categories, busy, onAdd, onRemove }) => {
  const [value, setValue] = useState('');
  const trimmed = value.trim();
  const exists = categories.some((category) => category.toLowerCase() === trimmed.toLowerCase());
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4 } }}>
      <Stack direction="row" alignItems="center" sx={{ px: 3, pt: 2.5 }}>
        <Typography variant="h6" fontWeight={900} sx={{ flex: 1 }}>Categories</Typography>
        <IconButton aria-label="Close" onClick={onClose}><Close /></IconButton>
      </Stack>
      <DialogContent sx={{ pt: 1 }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Group services so customers find them faster. Removing a category leaves its services uncategorized.
        </Typography>
        <Stack direction="row" spacing={1} component="form"
          onSubmit={(event) => { event.preventDefault(); if (trimmed && !exists) { onAdd(trimmed); setValue(''); } }}>
          <TextField size="small" fullWidth label="New category" value={value} disabled={busy}
            onChange={(event) => setValue(event.target.value)} inputProps={{ maxLength: 30 }}
            error={exists} helperText={exists ? 'That category already exists.' : undefined} />
          <Button type="submit" variant="contained" disabled={busy || !trimmed || exists} sx={{ fontWeight: 800 }}>Add</Button>
        </Stack>
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {categories.length ? categories.map((category) => (
            <Chip key={category} label={category} onDelete={busy ? undefined : () => onRemove(category)} sx={{ fontWeight: 700 }} />
          )) : <Typography variant="body2" color="text.secondary">No categories yet.</Typography>}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/** Services overview with add/edit dialog and categories. Used by the admin and super admin. */
const ServicesManager: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Service | null>(null);
  const [adding, setAdding] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [filter, setFilter] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const employeesQuery = useQuery<Employee[]>({ queryKey: ['allEmployees', tenantId], queryFn: () => fetchAllEmployees(tenantId) });
  const servicesQuery = useQuery<Service[]>({
    queryKey: ['services', tenantId], queryFn: () => fetchServices(tenantId), staleTime: 1000 * 60 * 5,
  });
  const configQuery = useQuery({ queryKey: ['businessConfig', tenantId], queryFn: () => getCurrentBusinessConfig(tenantId) });

  const ux = resolveAdminBookingUxConfig(configQuery.data ?? null);
  const staffLabel = ux.terminology.resourcePlural;
  const staffLabelSingular = ux.terminology.resourceSingular;
  const ownerId = configQuery.data?.paymentInfo?.owner?.trim() ?? '';
  const staff = useMemo(() => {
    const all = (employeesQuery.data ?? []).filter((employee) => employee.active !== false);
    return ux.bookingModel === 'resource' && ownerId ? all.filter((employee) => employee.id !== ownerId) : all;
  }, [employeesQuery.data, ux.bookingModel, ownerId]);
  const staffIds = useMemo(() => new Set(staff.map((person) => person.id)), [staff]);
  const categories = configQuery.data?.preferences?.serviceCategories ?? [];
  const services = servicesQuery.data ?? [];
  const visible = filter ? services.filter((service) => service.category === filter) : services;

  const categoriesMutation = useMutation({
    mutationFn: async ({ next, removed }: { next: string[]; removed?: string }) => {
      await updatePreferencesConfig({ ...(configQuery.data?.preferences ?? {}), serviceCategories: next }, tenantId);
      if (removed) {
        for (const service of services.filter((candidate) => candidate.category === removed)) {
          await updateService(service, { ...service, category: deleteField() as FieldValue as unknown as undefined }, undefined, tenantId);
        }
      }
      return { next, removed };
    },
    onSuccess: ({ next, removed }) => {
      queryClient.setQueryData(['businessConfig', tenantId], (current: typeof configQuery.data) => current ? {
        ...current, preferences: { ...current.preferences, serviceCategories: next },
      } : current);
      if (removed) {
        queryClient.setQueryData<Service[]>(['services', tenantId], (current) => (current ?? []).map((service) =>
          service.category === removed ? { ...service, category: undefined } : service));
        if (filter === removed) setFilter(null);
      }
    },
    onError: () => setToast({ message: 'Categories could not be updated.', severity: 'error' }),
  });

  if (employeesQuery.isLoading || servicesQuery.isLoading || configQuery.isLoading) return <LoadingSpinner label="Loading services" />;
  if (employeesQuery.isError || servicesQuery.isError || configQuery.isError) {
    return <Alert severity="error" sx={{ m: 2 }}>Services could not be loaded. Refresh to try again.</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, pb: { xs: 12, sm: 4 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={900} sx={{ fontSize: { xs: '1.6rem', sm: '2.1rem' } }}>Services</Typography>
          <Typography color="text.secondary" sx={{ mt: .5 }}>
            What customers can book, how long it takes, what it costs, and who performs it.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
          <Button variant="outlined" startIcon={<TuneRounded />} onClick={() => setCategoriesOpen(true)}
            sx={{ borderRadius: 3, fontWeight: 800 }}>Categories</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setAdding(true)}
            sx={{ borderRadius: 3, fontWeight: 900, px: 2.5 }}>Add service</Button>
        </Stack>
      </Stack>

      {categories.length ? (
        <Stack direction="row" spacing={1} sx={{ mt: 3, overflowX: 'auto', pb: .5 }}>
          {[null, ...categories].map((category) => (
            <Chip key={category ?? 'all'} label={category ?? `All (${services.length})`} clickable
              color={filter === category ? 'primary' : 'default'} variant={filter === category ? 'filled' : 'outlined'}
              onClick={() => setFilter(category)} sx={{ fontWeight: 800, flexShrink: 0 }} />
          ))}
          <Chip label="Edit categories" icon={<TuneRounded />} variant="outlined" onClick={() => setCategoriesOpen(true)}
            sx={{ display: { xs: 'inline-flex', sm: 'none' }, fontWeight: 800, flexShrink: 0 }} />
        </Stack>
      ) : null}

      {services.length === 0 ? (
        <Paper elevation={0} sx={{ mt: 3, p: { xs: 3, sm: 6 }, textAlign: 'center', borderRadius: 4,
          border: '2px dashed', borderColor: 'rgba(25,118,210,.25)', bgcolor: 'rgba(25,118,210,.03)' }}>
          <Typography variant="h6" fontWeight={900}>Add your first service</Typography>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 2.5 }}>Customers can book it as soon as it’s saved.</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setAdding(true)} sx={{ borderRadius: 3, fontWeight: 900 }}>
            Add service
          </Button>
        </Paper>
      ) : visible.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>No services in “{filter}” yet.</Typography>
      ) : (
        <Box sx={{ mt: 3, display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
          {visible.map((service) => (
            <ServiceCard key={service.id} service={service} staffLabel={staffLabel} staffLabelSingular={staffLabelSingular}
              staffCount={service.employees.filter((id) => staffIds.has(id)).length}
              onEdit={() => setEditing(service)} />
          ))}
        </Box>
      )}

      <Box sx={{ display: { xs: 'block', sm: 'none' }, position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 10,
        px: 2, pt: 1.25, pb: 'max(14px, env(safe-area-inset-bottom))', bgcolor: 'rgba(255,255,255,.96)',
        borderTop: '1px solid rgba(15,23,42,.10)', backdropFilter: 'blur(16px)' }}>
        <Button fullWidth variant="contained" startIcon={<Add />} onClick={() => setAdding(true)}
          sx={{ minHeight: 50, borderRadius: 3, fontWeight: 900 }}>Add service</Button>
      </Box>

      <ServiceFormDialog open={adding || Boolean(editing)} service={editing ?? undefined}
        onClose={() => { setAdding(false); setEditing(null); }}
        tenantId={tenantId} staff={staff} categories={categories} staffLabel={staffLabel}
        onSaved={(service, action) => setToast({ message: `“${service.title}” ${action}.`, severity: 'success' })} />
      <CategoriesDialog open={categoriesOpen} onClose={() => setCategoriesOpen(false)} categories={categories}
        busy={categoriesMutation.isPending}
        onAdd={(category) => categoriesMutation.mutate({ next: [...categories, category] })}
        onRemove={(category) => categoriesMutation.mutate({ next: categories.filter((existing) => existing !== category), removed: category })} />
      <Snackbar open={Boolean(toast)} autoHideDuration={3500} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default ServicesManager;
